import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../../Pages/UserPlan/ChoosePlanStyle.css";
import "./DropdownForNotiFy.css";
import { formatNotificationTime } from "../../Pages/utils/global";
import { AiOutlineDelete } from "react-icons/ai";

function DropdownForNotiFy({
  isOptionsVisible,
  setIsOptionsVisible,
  options,
  handleDeleteNotification,
  anchorRef,
  fetchMoreData,
  hasMore = false,
  isLoadingMore = false,
}) {
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const loadMoreRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({ top: 68, right: 36 });

  useEffect(() => {
    if (!isOptionsVisible) return;

    const updateDropdownPosition = () => {
      const anchor = anchorRef?.current;
      if (!anchor) {
        setDropdownStyle({ top: 68, right: 36 });
        return;
      }
      const rect = anchor.getBoundingClientRect();
      setDropdownStyle({
        top: rect.bottom + 8,
        right: Math.max(window.innerWidth - rect.right, 16),
      });
    };

    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);

    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
    };
  }, [isOptionsVisible, anchorRef]);

  useEffect(() => {
    if (!isOptionsVisible) return;

    const handleClickOutside = (event) => {
      const target = event.target;
      if (dropdownRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      setIsOptionsVisible(false);
    };

    document.addEventListener("click", handleClickOutside, true);

    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [isOptionsVisible, setIsOptionsVisible, anchorRef]);

  const handleLoadMore = useCallback(() => {
    if (!hasMore || isLoadingMore || !fetchMoreData) return;
    fetchMoreData();
  }, [hasMore, isLoadingMore, fetchMoreData]);

  useEffect(() => {
    if (!isOptionsVisible || !hasMore) return;

    const sentinel = loadMoreRef.current;
    const root = listRef.current;
    if (!sentinel || !root) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root,
        rootMargin: "80px",
        threshold: 0,
      },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isOptionsVisible, hasMore, handleLoadMore, options?.length]);

  if (!isOptionsVisible) return null;

  return createPortal(
    <>
      <div
        className="cp-dropdown-overlay"
        style={{ zIndex: 10100 }}
        onClick={() => setIsOptionsVisible(false)}
      />
      <div
        ref={dropdownRef}
        className="cp-profile-dropdown noti-dropdown"
        style={{
          top: dropdownStyle.top,
          right: dropdownStyle.right,
          zIndex: 10101,
        }}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="noti-dropdown-header">
          <h2 className="noti-dropdown-title">Notifications</h2>
          <button
            type="button"
            className="noti-dropdown-close"
            onClick={() => setIsOptionsVisible(false)}
            aria-label="Close notifications"
          >
            &#10005;
          </button>
        </div>

        <div className="cp-dropdown-divider" />

        <div className="noti-dropdown-body" ref={listRef}>
          {options?.length > 0 ? (
            <>
              {options.map((opt, ind) => (
                <div className="noti-item" key={opt._id || ind}>
                  <div className="noti-item-content">
                    <p className="noti-item-message">{opt.message}</p>
                    <span className="noti-item-time">
                      {formatNotificationTime(opt?.createdAt)}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="noti-item-delete"
                    onClick={() => handleDeleteNotification(opt._id)}
                    aria-label="Delete notification"
                  >
                    <AiOutlineDelete />
                  </button>
                </div>
              ))}
              {(hasMore || isLoadingMore) && (
                <div ref={loadMoreRef} className="noti-load-more">
                  {isLoadingMore ? "Loading…" : "Scroll for more"}
                </div>
              )}
            </>
          ) : isLoadingMore ? (
            <div className="noti-empty">
              <p>Loading notifications…</p>
            </div>
          ) : (
            <div className="noti-empty">
              <p>No notifications yet</p>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

export default DropdownForNotiFy;
